import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useId } from "react";

/*
 * Focus is a soft halo in the tenant's own ring colour (`--gq-color-1-ring`)
 * around the brand-coloured edge — visible in every theme without the hard
 * browser outline sitting a few pixels off the control.
 */
const CONTROL =
  "w-full rounded-md border bg-raised px-3 text-ink shadow-control placeholder:text-muted/60 " +
  "transition-[border-color,box-shadow] duration-150 hover:border-ink/25 " +
  "focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none " +
  "disabled:cursor-not-allowed disabled:bg-surface disabled:opacity-60";

export interface FieldProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
  children: (id: string, invalid: boolean) => ReactNode;
}

/**
 * Wraps any control with its label, hint and error, and wires up the ids so
 * screen readers announce the error with the field.
 */
export function Field({ label, error, hint, required, children }: FieldProps) {
  const id = useId();
  const invalid = Boolean(error);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-ink" htmlFor={id}>
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </label>
      {children(id, invalid)}
      {error ? (
        <p className="text-[13px] text-danger" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[13px] text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export function TextInput({ label, error, hint, required, ...rest }: TextInputProps) {
  return (
    <Field error={error} hint={hint} label={label} required={required}>
      {(id, invalid) => (
        <input
          aria-describedby={invalid ? `${id}-error` : undefined}
          aria-invalid={invalid}
          className={`${CONTROL} h-10 ${invalid ? "border-danger focus:border-danger" : "border-rule"}`}
          id={id}
          {...rest}
        />
      )}
    </Field>
  );
}

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

export function TextArea({ label, error, hint, required, rows = 3, ...rest }: TextAreaProps) {
  return (
    <Field error={error} hint={hint} label={label} required={required}>
      {(id, invalid) => (
        <textarea
          aria-describedby={invalid ? `${id}-error` : undefined}
          aria-invalid={invalid}
          className={`${CONTROL} py-2 ${invalid ? "border-danger focus:border-danger" : "border-rule"}`}
          id={id}
          rows={rows}
          {...rest}
        />
      )}
    </Field>
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
}

export function SelectField({
  label,
  error,
  hint,
  required,
  options,
  placeholder,
  ...rest
}: SelectFieldProps) {
  return (
    <Field error={error} hint={hint} label={label} required={required}>
      {(id, invalid) => (
        <select
          aria-describedby={invalid ? `${id}-error` : undefined}
          aria-invalid={invalid}
          className={`${CONTROL} h-10 ${invalid ? "border-danger focus:border-danger" : "border-rule"}`}
          id={id}
          {...rest}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}
